import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';


function Settings() {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, subRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/billing/subscription')
      ]);

      setUser(userRes.data.user);
      setSubscription(subRes.data.subscription);
    } catch (error) {
      toast.error('Failed to load account data');
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  const isSubscribed = user?.plan && user.plan !== 'free';
  const willCancel = subscription?.cancelAtPeriodEnd;

  return (
    <>
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Account Settings</h1>

      {/* Profile Information */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Profile Information</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <div className="text-gray-900">{user?.name || 'Not set'}</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <div className="text-gray-900">{user?.email}</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Member Since</label>
            <div className="text-gray-900">
              {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : 'Unknown'}
            </div>
          </div>
        </div>
      </div>

      {/* Subscription */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Subscription</h2>

        {/* Cancellation Warning */}
        {willCancel && (
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <svg className="h-6 w-6 text-yellow-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h3 className="text-yellow-900 font-semibold mb-1">Subscription Scheduled for Cancellation</h3>
                <p className="text-yellow-800 text-sm mb-2">
                  Your subscription will be canceled on{' '}
                  <strong>
                    {subscription?.currentPeriodEnd
                      ? new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })
                      : 'the end of your billing period'
                    }
                  </strong>.
                  You'll be downgraded to the Free plan (100 requests/month) after this date.
                </p>
                <Link
                  to="/billing"
                  className="text-yellow-700 hover:text-yellow-600 font-medium text-sm underline"
                >
                  Manage your subscription in billing →
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Plan</label>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-2xl font-bold text-indigo-600 capitalize">{user?.plan || 'Free'}</span>
                {subscription?.status && (
                  <span className={`ml-3 text-sm px-2 py-1 rounded ${subscription.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                    {subscription.status}
                  </span>
                )}
              </div>
              {!isSubscribed && (
                <Link
                  to="/pricing"
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 text-sm font-medium"
                >
                  Upgrade Plan
                </Link>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Quota</label>
            <div className="text-gray-900">{user?.monthlyQuota?.toLocaleString() || 0} requests/month</div>
          </div>

          {subscription?.currentPeriodEnd && !willCancel && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Next Billing Date</label>
              <div className="text-gray-900">
                {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>
          )}

          <div className="pt-4 border-t space-y-3">
            <div className="flex flex-wrap gap-3">
              <Link
                to="/pricing"
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm"
              >
                Manage Plan
              </Link>

              {isSubscribed && (
                <Link
                  to="/billing"
                  className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm"
                >
                  Manage Billing
                </Link>
              )}
            </div>
            <p className="text-xs text-gray-500">
              Change your subscription plan or manage billing details and invoices
            </p>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Need Help?</h2>
        <p className="text-gray-700 mb-4">
          Have questions about your account or subscription? We're here to help!
        </p>
        <div className="space-y-2 text-sm">
          <div>
            <strong>Email:</strong> support@snaptura.online
          </div>
          <div>
            <Link to="/docs" className="text-indigo-600 hover:text-indigo-500 font-medium">
              View Documentation →
            </Link>
          </div>
        </div>
      </div>
    </div>

    <Footer className="hidden md:block" />
    </>
  );
}

export default Settings;